// SNIGJ - Login Page with Government Logo, Social Login, Marquee Info Banner
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Scale, Mail, Lock, Sun, Moon, Globe, AlertCircle, Eye, EyeOff, Apple } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Default government logo placeholder (armoiries RDC)
const DEFAULT_GOV_LOGO = '/logo192.png';

// Default legal information for the marquee banner
const DEFAULT_LEGAL_INFO = [
  "⚖️ CODE PÉNAL - Art. 79: Le vol simple est puni de 1 mois à 5 ans de servitude pénale principale.",
  "📜 PROCÉDURE PÉNALE - Art. 28: La garde à vue ne peut excéder 48 heures, renouvelable une fois sur autorisation du Procureur.",
  "🏛️ CONSTITUTION - Art. 19: Tout citoyen a droit à un avocat dès son interpellation.",
  "⚖️ CODE CIVIL - Art. 258: Tout fait quelconque de l'homme qui cause un dommage à autrui oblige celui par la faute duquel il est arrivé à le réparer.",
  "📜 PROCÉDURE CIVILE - Art. 1: L'action en justice est ouverte à tous ceux qui ont un intérêt légitime.",
  "🏛️ LOI ORGANIQUE - Art. 2: Les tribunaux de paix connaissent des infractions punissables de 5 ans au maximum.",
  "⚖️ CODE PÉNAL - Art. 156: L'abus de confiance est puni de 3 mois à 5 ans de servitude pénale.",
  "📜 DROITS DE L'HOMME - Toute personne arrêtée doit être informée des motifs de son arrestation.",
  "🏛️ ACCÈS À LA JUSTICE - Pour déposer une plainte, rendez-vous au commissariat ou parquet le plus proche.",
  "⚖️ URGENCE JUDICIAIRE - Appelez le 911 pour toute urgence ou violation de vos droits."
];

// Google Login Button Component - uses the useGoogleLogin hook
const GoogleLoginButton = ({ onSuccess, onError, loading, setLoading, language }) => {
  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        // Get user info from Google using the access token
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        });
        const userInfo = await userInfoResponse.json();
        
        // Send to backend with user info
        await onSuccess({
          access_token: tokenResponse.access_token,
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture,
          sub: userInfo.sub
        });
      } catch (err) {
        console.error('Error getting user info:', err);
        onError(err);
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google login error:', error);
      onError(error);
      setLoading(false);
    },
    onNonOAuthError: (error) => {
      console.error('Non-OAuth error:', error);
      // This handles popup closed or other non-OAuth errors
      if (error.type === 'popup_closed') {
        toast.info(language === 'fr' ? 'Connexion annulée' : 'Login cancelled');
      }
      setLoading(false);
    }
  });

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full h-11"
      onClick={() => googleLogin()}
      disabled={loading}
      data-testid="google-login-btn"
    >
      {loading ? (
        <div className="w-5 h-5 mr-2 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      )}
      Google
    </Button>
  );
};

// Inner component that contains all the login page logic
const LoginPageContent = ({ googleClientId, branding, setBranding }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, language, updateLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const from = location.state?.from || '/home';

  // Get the marquee text based on source
  const getMarqueeText = useCallback(() => {
    if (!branding.showMarqueeBanner) return '';
    
    switch (branding.marqueeBannerSource) {
      case 'text':
        return branding.marqueeBannerText || DEFAULT_LEGAL_INFO.join(' | ');
      case 'library':
        return branding.marqueeBannerLibraryText || DEFAULT_LEGAL_INFO.join(' | ');
      case 'file':
        return branding.marqueeBannerFileText || DEFAULT_LEGAL_INFO.join(' | ');
      case 'default':
      default:
        return DEFAULT_LEGAL_INFO.join(' | ');
    }
  }, [branding]);

  // Handle Google OAuth success - receives user info from popup
  const handleGoogleSuccess = async (googleUserInfo) => {
    setError('');
    try {
      // Call backend with Google user info
      const result = await loginWithGoogle(googleUserInfo);
      if (result.success) {
        toast.success(language === 'fr' ? 'Connexion réussie' : 'Login successful');
        navigate(from, { replace: true });
      } else {
        setError(result.error || (language === 'fr' ? 'Erreur de connexion Google' : 'Google login error'));
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError(language === 'fr' ? 'Erreur de connexion Google' : 'Google login error');
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google OAuth error:', error);
    setError(language === 'fr' ? 'Échec de la connexion Google' : 'Google login failed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        toast.success(language === 'fr' ? 'Connexion réussie' : 'Login successful');
        navigate(from, { replace: true });
      } else {
        setError(result.error || (language === 'fr' ? 'Identifiants invalides' : 'Invalid credentials'));
      }
    } catch (err) {
      setError(language === 'fr' ? 'Erreur de connexion' : 'Login error');
    } finally {
      setLoading(false);
    }
  };

  // Marquee speed - use custom speed value (in seconds) or preset
  const getMarqueeAnimation = () => {
    if (branding.marqueeBannerSpeedValue) {
      return `${branding.marqueeBannerSpeedValue}s`;
    }
    const speeds = {
      slow: '120s',
      normal: '80s',
      fast: '40s'
    };
    return speeds[branding.marqueeBannerSpeed] || '80s';
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Marquee Banner - Scrolling Information */}
      {branding.showMarqueeBanner && (
        <div 
          className="w-full py-2 text-white text-sm overflow-hidden"
          style={{ backgroundColor: branding.marqueeBannerColor || '#1e3a5f' }}
        >
          <div 
            className="whitespace-nowrap animate-marquee"
            style={{ 
              animation: `marquee ${getMarqueeAnimation()} linear infinite`,
            }}
          >
            {getMarqueeText()}
            <span className="mx-16">{getMarqueeText()}</span>
          </div>
          <style>{`
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-marquee {
              display: inline-block;
              padding-left: 100%;
            }
          `}</style>
        </div>
      )}

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Left side - Hero Image (hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <img
            src={branding.loginImageUrl || 'https://images.pexels.com/photos/6077665/pexels-photo-6077665.jpeg'}
            alt="Justice"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/70" />
          <div className="relative z-10 flex flex-col justify-center p-12">
            <div className="flex items-center gap-4 mb-8">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="Logo" className="w-16 h-16 object-contain" />
              ) : (
                <div className="w-16 h-16 rounded bg-yellow-400 flex items-center justify-center">
                  <Scale className="w-10 h-10 text-slate-900" />
                </div>
              )}
              <div>
                <h1 className="font-heading text-4xl font-black text-white tracking-tight">
                  {branding.logoText || 'SNIGJ'}
                </h1>
                <p className="text-slate-300 text-sm tracking-wider">
                  {branding.logoSubtext || t(language, 'common.republic')}
                </p>
              </div>
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-4">
              {branding.loginHeaderText || t(language, 'auth.subtitle')}
            </h2>
            <p className="text-slate-300 max-w-md">
              {language === 'fr' 
                ? 'Plateforme unifiée de gestion des procédures judiciaires, pénitentiaires et administratives.'
                : 'Unified platform for managing judicial, prison and administrative procedures.'}
            </p>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="flex-1 flex flex-col min-h-screen lg:min-h-0">
          {/* Top bar */}
          <div className="flex justify-end items-center gap-3 p-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateLanguage(language === 'fr' ? 'en' : 'fr')}
              data-testid="login-lang-toggle"
            >
              <Globe className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="login-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>

          {/* Government Logo - ABOVE the form, centered */}
          <div className="flex justify-center py-6">
            <img 
              src={branding.governmentLogoUrl || DEFAULT_GOV_LOGO} 
              alt="Armoiries RDC" 
              style={{ 
                width: `${branding.governmentLogoSize || 120}px`,
                height: `${branding.governmentLogoSize || 120}px`
              }}
              className="object-contain drop-shadow-lg"
              onError={(e) => {
                e.target.src = DEFAULT_GOV_LOGO;
              }}
            />
          </div>

          {/* Form container */}
          <div className="flex-1 flex items-start justify-center px-4 sm:px-8">
            <Card className="w-full max-w-md border-0 shadow-xl">
              <CardHeader className="text-center pb-2">
                {/* Mobile: Show app logo */}
                <div className="lg:hidden flex justify-center mb-2">
                  {branding.logoUrl ? (
                    <img src={branding.logoUrl} alt="Logo" className="w-12 h-12 object-contain" />
                  ) : (
                    <div className="w-12 h-12 rounded bg-yellow-400 flex items-center justify-center">
                      <Scale className="w-7 h-7 text-slate-900" />
                    </div>
                  )}
                </div>
                
                <CardTitle className="font-heading text-xl sm:text-2xl">
                  {t(language, 'auth.welcome')}
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  {branding.loginFooterText || 'GROUPE KAZAM'}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-2">
                {error && (
                  <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t(language, 'auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="nom@justice.gouv.cd"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label htmlFor="password">{t(language, 'auth.password')}</Label>
                      <Link to="/forgot-password" className="text-xs text-yellow-600 hover:underline">
                        {t(language, 'auth.forgotPassword')}
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? t(language, 'common.loading') : t(language, 'auth.loginButton')}
                  </Button>
                </form>

                {/* Social Login Section */}
                <div className="mt-6">
                  <div className="relative mb-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-background text-muted-foreground text-xs">
                        {language === 'fr' ? 'ou continuer avec' : 'or continue with'}
                      </span>
                    </div>
                  </div>

                  {/* Google and Apple buttons - SIDE BY SIDE - Same style */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Google Login Button - Uses popup flow */}
                    {googleClientId ? (
                      <GoogleLoginButton
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        loading={googleLoading}
                        setLoading={setGoogleLoading}
                        language={language}
                      />
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-11"
                        onClick={() => toast.info(language === 'fr' ? 'Google OAuth non configuré' : 'Google OAuth not configured')}
                        data-testid="google-login-btn"
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Google
                      </Button>
                    )}

                    {/* Apple Login Button */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-11 bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 border-0"
                      onClick={() => toast.info(language === 'fr' ? 'Apple Sign-In sera bientôt disponible' : 'Apple Sign-In coming soon')}
                      data-testid="apple-login-btn"
                    >
                      <Apple className="w-5 h-5 mr-2" />
                      Apple
                    </Button>
                  </div>
                </div>

                <p className="text-center text-sm text-muted-foreground mt-6">
                  {t(language, 'auth.noAccount')}{' '}
                  <Link to="/register" className="text-yellow-600 hover:underline font-medium" data-testid="register-link">
                    {t(language, 'auth.registerButton')}
                  </Link>
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="p-4 text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} {branding.loginFooterText || `GROUPE KAZAM`}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main LoginPage component - wraps content with GoogleOAuthProvider
const LoginPage = () => {
  const [googleClientId, setGoogleClientId] = useState('');
  const [branding, setBranding] = useState({
    logoUrl: '',
    governmentLogoUrl: DEFAULT_GOV_LOGO,
    governmentLogoSize: 120,
    logoText: 'SNIGJ',
    logoSubtext: 'RDC',
    loginImageUrl: 'https://images.pexels.com/photos/6077665/pexels-photo-6077665.jpeg',
    loginHeaderText: 'Système National Intégré de Gestion de la Justice',
    loginFooterText: 'République Démocratique du Congo - Ministère de la Justice',
    showMarqueeBanner: true,
    marqueeBannerSource: 'default',
    marqueeBannerText: '',
    marqueeBannerSpeed: 'normal',
    marqueeBannerSpeedValue: null,
    marqueeBannerColor: '#1e3a5f'
  });

  // Load branding and OAuth config on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await api.get('/admin/branding');
        if (response.data && Object.keys(response.data).length > 0) {
          setBranding(prev => ({ ...prev, ...response.data }));
          if (response.data.googleClientId) {
            setGoogleClientId(response.data.googleClientId);
          }
        }
      } catch (error) {
        console.log('Using default branding');
      }
    };
    
    const loadConfig = async () => {
      try {
        const response = await api.get('/config/oauth');
        if (response.data?.google_client_id) {
          setGoogleClientId(response.data.google_client_id);
        }
      } catch (error) {
        console.log('OAuth config not available');
      }
    };
    
    loadBranding();
    loadConfig();
  }, []);

  // If Google Client ID is available, wrap with GoogleOAuthProvider
  if (googleClientId) {
    return (
      <GoogleOAuthProvider clientId={googleClientId}>
        <LoginPageContent 
          googleClientId={googleClientId} 
          branding={branding} 
          setBranding={setBranding} 
        />
      </GoogleOAuthProvider>
    );
  }

  // Render without Google OAuth provider if no client ID
  return (
    <LoginPageContent 
      googleClientId={googleClientId} 
      branding={branding} 
      setBranding={setBranding} 
    />
  );
};

export default LoginPage;
